import { Dispatch, SetStateAction, SyntheticEvent } from 'react';
import { StyledTab, StyledTabs } from './DateRangeControl.styles';
import { PERIODS } from './constants';
import { Period } from './types';
import { Box } from '@mui/material';
import { DateRange } from '../../DateRange/DateRange';
import { useTranslation } from 'react-i18next';
import { Quarters } from '../../../pages/GeneralInput/DateRangesBlock';
import { DateRangeState } from './useDateRange';
import { getDateRange } from './helpers';

type Props = {
  dateRange: DateRangeState;
  setDateRange: Dispatch<SetStateAction<DateRangeState>>;
};

export const DateRangeControl = ({ dateRange, setDateRange }: Props) => {
  const { t } = useTranslation();

  const now = new Date();
  const currentYear = now.getFullYear();
  const yearsOptions = [currentYear - 2, currentYear - 1, currentYear].map((item) =>
    item.toString(),
  );

  const handleTabChange = (_event: SyntheticEvent, newValue: Period) => {
    const newDateRange: DateRangeState = { name: newValue };

    if (newValue === 'QUARTER') {
      const { year, quarter, dateString } = getDateRange({ period: 'QUARTER' });
      newDateRange.dateString = dateString;
      newDateRange.year = year;
      newDateRange.quarter = quarter;
    } else if (newValue === '1Y') {
      const { year, dateString } = getDateRange({ period: '1Y' });
      newDateRange.dateString = dateString;
      newDateRange.year = year;
      newDateRange.quarter = undefined;
    } else if (newValue === 'YTD') {
      const { year, dateString } = getDateRange({ period: 'YTD' });
      newDateRange.dateString = dateString;
      newDateRange.year = year;
    }

    setDateRange(newDateRange);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <StyledTabs value={dateRange.name} onChange={handleTabChange} centered>
        {PERIODS.map((p) => (
          <StyledTab key={p} label={p} value={p} />
        ))}
      </StyledTabs>
      {dateRange.name === 'QUARTER' && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 2,
            alignItems: 'center',
          }}
        >
          <DateRange
            title={t('year')}
            items={yearsOptions}
            value={String(dateRange.year)}
            onChange={(e) => {
              const { year, quarter, dateString } = getDateRange({
                period: 'QUARTER',
                year: Number(e.target.value),
                quarter: dateRange.quarter,
              });

              setDateRange((prev) => {
                return { ...prev, ...{ year: year, quarter: quarter, dateString: dateString } };
              });
            }}
          />
          <DateRange
            title={t('quarter')}
            items={Quarters}
            value={Quarters[dateRange.quarter - 1]}
            onChange={(e) => {
              const { year, quarter, dateString } = getDateRange({
                period: 'QUARTER',
                year: dateRange.year,
                quarter: Quarters.indexOf(e.target.value) + 1,
              });

              setDateRange((prev) => {
                return { ...prev, ...{ year: year, quarter: quarter, dateString: dateString } };
              });
            }}
          />
        </Box>
      )}
      {dateRange.name === '1Y' && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
          }}
        >
          <DateRange
            title={t('year')}
            items={yearsOptions}
            value={String(dateRange.year)}
            onChange={(e) => {
              const { year, dateString } = getDateRange({
                period: '1Y',
                year: Number(e.target.value),
              });

              setDateRange((prev) => {
                return { ...prev, ...{ year: year, quarter: undefined, dateString: dateString } };
              });
            }}
          />
        </Box>
      )}
    </Box>
  );
};
